import type { InjectionKey, Ref } from "vue";
import { inject, onBeforeUnmount, watch } from "vue";

/**
 * Lets content of a dialog that is teleported elsewhere (a floating-vue popper renders under `body`) join the
 * dialog's focus trap. Without it the trap pulls focus straight back out of such a panel, e.g. out of a search input.
 */
export interface DialogFocusContainers {
  add: (element: HTMLElement) => void
  remove: (element: HTMLElement) => void
}

export const DIALOG_FOCUS_CONTAINERS_KEY: InjectionKey<DialogFocusContainers> = Symbol("DialogFocusContainers");

/** Registers the element with the surrounding dialog's focus trap while it is set. Outside a dialog it does nothing. */
export function useDialogFocusContainer(element: Ref<HTMLElement | null>): void {
  const containers = inject(DIALOG_FOCUS_CONTAINERS_KEY, null);
  if (!containers) {
    return;
  }

  const stop = watch(element, (current, previous) => {
    if (previous) {
      containers.remove(previous);
    }
    if (current) {
      containers.add(current);
    }
  }, { flush: "sync" });

  onBeforeUnmount(() => {
    // Stopped first, so the owner clearing the element while it unmounts does not remove it a second time.
    stop();
    if (element.value) {
      containers.remove(element.value);
    }
  });
}
